import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import CommonMultiSelect from './CommonMultiSelect';
import CommonDropdown from './CommonDropdown';
import CommonButton from './CommonButton';

/**
 * Config-driven form modal — a reusable wrapper over react-bootstrap Modal,
 * styled with the global light theme tokens (see src/styles/theme.css).
 * Can also be used as a plain display modal (pass `title` + `children`, no
 * `fields`/`buttons`). The modal owns its form via
 * react-hook-form: pass a `fields` config (with validation rules) and a
 * `buttons` config, and it renders, validates, and reports errors for you.
 *
 * Usage:
 *   <CommonModal
 *       show={open}
 *       onHide={() => setOpen(false)}
 *       title="Add Report Recipient"
 *       subtitle="They'll receive scheduled reports by email."
 *       fields={[
 *           { name: 'email',       label: 'Email',       type: 'email',    placeholder: 'name@company.com', required: true },
 *           { name: 'name',        label: 'Name',        type: 'text',     half: true, required: true },
 *           { name: 'designation', label: 'Designation', type: 'text',     half: true },
 *           { name: 'role',        label: 'Role',        type: 'select',   options: [{ value: 'a', label: 'A' }] },
 *           { name: 'is_active',   label: 'Active',      type: 'checkbox' },
 *       ]}
 *       defaultValues={{ email: '', name: '', designation: '', is_active: true }}
 *       onSubmit={(data) => save(data)}       // receives validated values
 *       error={submitError}                    // server-side error string
 *       buttons={[
 *           { text: 'Cancel',        variant: 'secondary', onClick: () => setOpen(false), disabled: busy },
 *           { text: 'Add Recipient', variant: 'primary',   type: 'submit', loading: busy, loadingText: 'Saving…' },
 *       ]}
 *   />
 *
 * Field config: { name, label, type, placeholder, options, half, disabled, rows,
 *                 required, pattern, minLength, maxLength, validate, onChange, suffixButton }
 *   type ∈ text | email | password | number | tel | url | textarea | select | multiselect | checkbox   (default: text)
 *   half: true  → renders at 50% width so two consecutive fields sit side by side
 *   options     → [{ value, label }] for `select` and `multiselect`
 *   `type: 'multiselect'` renders a checkbox dropdown; its form value is an
 *   array of selected `value`s — default it to [] in defaultValues.
 *   `onChange` (select fields only): (value) => void, fired alongside the
 *   field's own react-hook-form update — lets the caller react to this one
 *   field's own selection (e.g. toggle another field's visibility/type)
 *   without needing live access to the form's internal state. Optional; a
 *   field with no `onChange` behaves exactly as before.
 *   `suffixButton` (text-like fields only): { text, onClick, variant, style } — renders
 *   a CommonButton to the right of the input, in the same row (e.g. a
 *   "Cancel" action to leave a custom-text mode). Optional; a field with no
 *   `suffixButton` renders exactly as before.
 *   `style` (text-like fields only, top-level on the field config): inline style
 *   applied directly to the `<input>` — an escape hatch for cases like pinning
 *   a fixed height so it visually matches an adjacent `suffixButton`/dropdown
 *   whose own height comes from a conflicting stylesheet rule elsewhere in the
 *   app. Optional; a field with no `style` renders exactly as before.
 *   Validation (react-hook-form rules): `required` accepts true or a custom message;
 *   `pattern`/`minLength`/`maxLength`/`validate` are passed straight through.
 *   `type: 'email'` auto-applies an email pattern unless one is supplied.
 *   `type: 'password'` renders a show/hide eye toggle.
 *   `validate` receives (value, allValues) — the 2nd arg enables cross-field
 *   rules, e.g. validate: (v, all) => v === all.newPassword || 'Passwords must match'.
 *
 * Button config: { text, onClick, variant, type, loading, loadingText, disabled }
 *   variant ∈ primary | secondary | danger   (default: secondary)
 *   type    ∈ button | submit                 (default: button); a `submit` button
 *             triggers validation before onSubmit runs.
 *
 * Rendering mode: by default this is a pop-up Modal driven by `show`/`onHide`.
 *   Pass `inline` to render the exact same form (fields / buttons / validation /
 *   onSubmit) as a plain on-screen card instead — no backdrop, no portal, no
 *   show/hide. Inline forms reset on mount, so remount via a changing `key` to
 *   clear them after a submit. Defaults to modal, so existing callers are
 *   unaffected.
 *
 * Sizing (pick one): boolean shorthands `small` (narrow, sm), `mid` (roomy, lg),
 *   `fullscreen` (fills the viewport). Or pass `size` ∈ 'sm' | 'lg' | 'xl'
 *   directly (explicit `size` wins over the shorthands). Default = medium.
 *   `fullscreen` also accepts a breakpoint ('sm-down' | 'md-down' | 'lg-down' |
 *   'xl-down' | 'xxl-down'). Use fullscreen/mid when the form has room-hungry
 *   controls (e.g. multi-select dropdowns) that shouldn't be cramped.
 */
const EMAIL_PATTERN = { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' };

const EYE_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const EYE_OFF_ICON = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

function buildRules(field) {
    const rules = {};
    if (field.required) {
        const requiredMsg = typeof field.required === 'string' ? field.required : `${field.label} is required`;
        // A multiselect value is an array — an empty [] is truthy, so the
        // built-in `required` never fires. Enforce non-empty via validate.
        if (field.type === 'multiselect') {
            rules.validate = (v) => (Array.isArray(v) && v.length > 0) || requiredMsg;
        } else {
            rules.required = requiredMsg;
        }
    }
    if (field.pattern) rules.pattern = field.pattern;
    else if (field.type === 'email') rules.pattern = EMAIL_PATTERN;
    if (field.minLength) rules.minLength = field.minLength;
    if (field.maxLength) rules.maxLength = field.maxLength;
    if (field.validate) rules.validate = field.validate;   // caller-supplied validate wins
    return rules;
}

function Field({ field, register, control, error }) {
    const { name, label, type = 'text', placeholder, options = [], required, half, disabled, rows } = field;
    const [reveal, setReveal] = useState(false);
    // select + multiselect are controlled via RHF Controller (custom
    // popover components, not native inputs); everything else uses register.
    const reg = (type === 'multiselect' || type === 'select') ? null : register(name, buildRules(field));

    if (type === 'checkbox') {
        return (
            <label className="cmn-modal-checkbox">
                <input type="checkbox" disabled={disabled} {...reg} />
                <span>{label}</span>
            </label>
        );
    }

    let inputEl;
    if (type === 'select') {
        inputEl = (
            <Controller
                name={name}
                control={control}
                rules={buildRules(field)}
                render={({ field: f }) => (
                    <CommonDropdown value={f.value} onChange={(v) => { f.onChange(v); field.onChange?.(v); }}
                        options={options} placeholder={placeholder || 'Select…'}
                        disabled={disabled} ariaLabel={label} minWidth={0} style={{ width: '100%' }} />
                )}
            />
        );
    } else if (type === 'multiselect') {
        inputEl = (
            <Controller
                name={name}
                control={control}
                rules={buildRules(field)}
                render={({ field: f }) => (
                    <CommonMultiSelect value={f.value} onChange={f.onChange}
                        options={options} placeholder={placeholder} disabled={disabled} />
                )}
            />
        );
    } else if (type === 'textarea') {
        inputEl = <textarea className="cmn-modal-input cmn-modal-textarea" placeholder={placeholder} disabled={disabled} rows={rows || 3} {...reg} />;
    } else if (type === 'password') {
        inputEl = (
            <div className="cmn-modal-input-wrap">
                <input className="cmn-modal-input" type={reveal ? 'text' : 'password'} placeholder={placeholder} disabled={disabled} {...reg} />
                <button type="button" className="cmn-modal-eye" tabIndex={-1}
                    onClick={() => setReveal(r => !r)} aria-label={reveal ? 'Hide password' : 'Show password'}>
                    {reveal ? EYE_OFF_ICON : EYE_ICON}
                </button>
            </div>
        );
    } else {
        const baseInput = <input className="cmn-modal-input" type={type} placeholder={placeholder} disabled={disabled} min={field.min} max={field.max} style={field.style} {...reg} />;
        inputEl = field.suffixButton ? (
            <div className="cmn-modal-input-row">
                <div className="cmn-modal-input-row-main">{baseInput}</div>
                <CommonButton
                    type="button"
                    text={field.suffixButton.text}
                    variant={field.suffixButton.variant}
                    onClick={field.suffixButton.onClick}
                    style={field.suffixButton.style}
                />
            </div>
        ) : baseInput;
    }

    return (
        <div
            className={`cmn-modal-field${half ? ' cmn-modal-field--half' : ''}`}
            // Optional escape hatch for an asymmetric pair (e.g. a narrow
            // country-code select beside a wider phone-number field) — `half`
            // only ever gives an even 50/50 split. Inline style beats the
            // `.cmn-modal-field--half` class either way, so `half` can stay
            // set alongside this with no conflict. `minWidth: 0` overrides
            // the flex item's default `min-width: auto`, which otherwise lets
            // a child's own intrinsic content width (e.g. a dropdown trigger)
            // force this column wider than its flex-basis — pushing the row
            // past 100% and wrapping its sibling onto a new line.
            style={field.flexBasis ? { flex: `0 1 ${field.flexBasis}`, minWidth: 0 } : undefined}
        >
            <label className="cmn-modal-label">{label}{required && <span className="cmn-modal-req">*</span>}</label>
            {inputEl}
            {error && <p className="cmn-modal-field-err">{error.message}</p>}
        </div>
    );
}

function CommonModal({
    show, onHide, title, subtitle,
    fields = [], defaultValues = {}, onSubmit, error, children,
    buttons = [], size, centered = true, fullscreen = false,
    small = false, mid = false,
    // When true, render the exact same form as a plain on-screen card instead
    // of a react-bootstrap Modal — no backdrop, no portal, no show/hide. All
    // other props (fields / buttons / onSubmit / validation) behave identically.
    // Defaults to false, so every existing caller keeps opening as a modal.
    inline = false,
    // Optional: called with the full form values whenever any field changes —
    // for live previews (e.g. a summary card that recomputes as the user types).
    onValuesChange,
    formRef,
}) {
    const resolvedSize = size || (small ? 'sm' : mid ? 'lg' : undefined);
    const { register, control, handleSubmit, reset, setValue, getValues, watch, formState: { errors } } = useForm({ defaultValues });

    // Reset to fresh defaults each time the modal opens. Inline forms have no
    // `show` toggle, so they reset once on mount (remount via `key` to re-reset).
    useEffect(() => {
        if (inline || show) reset(defaultValues);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, inline]);

    // Live values subscription for `onValuesChange`. Keep the latest callback in
    // a ref so the watch subscription itself stays stable (a caller can pass an
    // inline function without re-subscribing every render).
    const onValuesChangeRef = useRef(onValuesChange);
    onValuesChangeRef.current = onValuesChange;
    useEffect(() => {
        if (!onValuesChangeRef.current) return undefined;
        const sub = watch((values) => onValuesChangeRef.current?.(values));
        return () => sub.unsubscribe();
    }, [watch]);

    const submit = (data) => { if (onSubmit) onSubmit(data); };
    // Keep the latest onSubmit in a ref so the exposed `submit()` below always
    // validates + calls the current handler (not a stale closure) even though
    // the bound handleSubmit itself stays stable.
    const submitRef = useRef(submit);
    submitRef.current = submit;

    // Optional imperative escape hatch: pass a ref and CommonModal stashes
    // { setValue, getValues, reset, submit } onto it, for callers that need to
    // read or write specific field values from outside (e.g. a "Cancel" action
    // reverting a custom-text field back to a dropdown default, or an AI assist
    // panel that reads the typed description and writes back the suggested
    // fields), or to trigger this form's own validation + submit from an
    // external button (`submit()` runs the same handleSubmit the footer buttons
    // would — validating and showing inline errors — then calls onSubmit) — none
    // of which is otherwise possible, since react-hook-form's own
    // setValue/getValues/handleSubmit aren't exposed.
    useEffect(() => {
        if (formRef) formRef.current = { setValue, getValues, reset, submit: handleSubmit((d) => submitRef.current(d)) };
    }, [formRef, setValue, getValues, reset, handleSubmit]);

    // The form markup is identical for both modes; only the outer wrapper
    // (Modal vs. plain div) and the header close button differ.
    const formBody = (
        <form className="cmn-modal-form" onSubmit={handleSubmit(submit)} noValidate>
            <div className="cmn-modal-header">
                <div>
                    <h3 className="cmn-modal-title">{title}</h3>
                    {subtitle && <p className="cmn-modal-subtitle">{subtitle}</p>}
                </div>
                {!inline && <button type="button" className="cmn-modal-close" onClick={onHide} aria-label="Close">×</button>}
            </div>

            <div className="cmn-modal-body">
                <div className="cmn-modal-fields">
                    {fields.map(f => (
                        <Field key={f.name} field={f} register={register} control={control} error={errors[f.name]} />
                    ))}
                </div>
                {children}
                {error && <p className="cmn-modal-error">{error}</p>}
            </div>

            {buttons.length > 0 && (
                <div className="cmn-modal-footer">
                    {buttons.map((b, i) => (
                        <CommonButton
                            key={i}
                            type={b.type || 'button'}
                            variant={b.variant }
                            text={b.loading ? (b.loadingText || 'Please wait…') : b.text}
                            onClick={b.onClick}
                            disable={b.disabled || b.loading}
                        />
                    ))}
                </div>
            )}
        </form>
    );

    // Inline mode — same form, rendered as an on-screen card.
    if (inline) {
        return (
            <div className="cmn-modal-content cmn-modal-content--inline">
                <style>{CMN_MODAL_CSS}</style>
                {formBody}
            </div>
        );
    }

    // Default — unchanged modal behaviour.
    return (
        <Modal show={show} onHide={onHide} centered={fullscreen ? false : centered} size={resolvedSize} fullscreen={fullscreen || undefined} contentClassName="cmn-modal-content">
            <style>{CMN_MODAL_CSS}</style>
            {formBody}
        </Modal>
    );
}

const CMN_MODAL_CSS = `
.cmn-modal-content {
    background: var(--color-modal) !important;
    border: 1px solid var(--color-border) !important;
    border-radius: var(--radius-lg) !important;
    box-shadow: var(--shadow-lg) !important;
    font-family: var(--font-family);
    overflow: hidden;
}
/* Inline mode — the same content rendered directly in the page as a plain
   card (no modal backdrop / heavy shadow / viewport-capped scrolling). */
.cmn-modal-content--inline {
margin-top: 6px;
    background: var(--color-surface) !important;
    box-shadow: var(--shadow-sm) !important;
    width: 100%;
}
.cmn-modal-content--inline .cmn-modal-body { max-height: none; overflow: visible; }
.cmn-modal-form { display: flex; flex-direction: column; }
.cmn-modal-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 20px 24px 14px; border-bottom: 1px solid var(--color-border);
    background: #d3e3fd8c;
}
.cmn-modal-title { margin: 0; color: var(--color-text-primary); font-size: 17px; font-weight: 600; }
.cmn-modal-subtitle { margin: 4px 0 0; color: #1c343f; font-size: 12.5px; line-height: 1.5; }
.cmn-modal-close {
    background: transparent; border: none; color: var(--color-text-muted); font-size: 24px; line-height: 1;
    cursor: pointer; padding: 0 4px; margin: -4px -4px 0 12px; transition: color 0.15s ease;
}
.cmn-modal-close:hover { color: var(--color-text-primary); }

.cmn-modal-body { padding: 20px 24px; max-height: 82vh; overflow-y: auto; overflow-x: auto; color: var(--color-text-primary); }
.cmn-modal-fields { display: flex; flex-wrap: wrap; gap: 16px; }
.cmn-modal-field { flex: 1 1 100%; display: flex; flex-direction: column; }
/* flex-grow: 0 -- a half field must always render at exactly 50%, even
   when it's the odd one out with no sibling left to share its row (e.g. a
   13th half field after 12 others paired evenly). With grow:1, that lone
   field silently stretched to fill the whole row's leftover space instead
   of staying half-width with blank space beside it. */
.cmn-modal-field--half { flex: 0 1 calc(50% - 8px); }
.cmn-modal-label { color: var(--color-text-secondary); font-size: 12px; font-weight: 500; margin-bottom: 6px; }
.cmn-modal-req { color: var(--color-danger); margin-left: 3px; }
.cmn-modal-input {
    width: 100%; background: var(--color-surface); border: 1px solid var(--color-border-strong); color: var(--color-text-primary);
    border-radius: var(--radius-sm); font-size: 13px; padding: 9px 12px; outline: none;
    font-family: var(--font-family); transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.cmn-modal-input::placeholder { color: var(--color-text-muted); }
.cmn-modal-input:hover:not(:disabled):not(:focus) { border-color: var(--color-text-muted); }
.cmn-modal-input:focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-soft); }
.cmn-modal-input:disabled { opacity: 0.6; cursor: not-allowed; }
.cmn-modal-textarea {
    display: block;
    resize: vertical;
    min-height: 70px;
    padding: 11px 14px;
    line-height: 1.65;
    white-space: pre-wrap;
}
select.cmn-modal-input { cursor: pointer; }

.cmn-modal-input-row { display: flex; gap: 8px; align-items: flex-start; }
.cmn-modal-input-row-main { flex: 1 1 auto; min-width: 0; }

.cmn-modal-input-wrap { position: relative; display: flex; align-items: center; }
.cmn-modal-input-wrap .cmn-modal-input { padding-right: 40px; }
.cmn-modal-eye {
    position: absolute; right: 8px; display: flex; align-items: center; justify-content: center;
    background: transparent; border: none; color: var(--color-text-muted); cursor: pointer; padding: 4px;
    transition: color 0.15s ease;
}
.cmn-modal-eye:hover { color: var(--color-text-secondary); }

.cmn-modal-checkbox {
    flex: 1 1 100%; display: flex; align-items: center; gap: 9px; cursor: pointer;
    color: var(--color-text-secondary); font-size: 13px; font-weight: 500; user-select: none;
}
.cmn-modal-checkbox input { width: 16px; height: 16px; cursor: pointer; accent-color: var(--color-primary); }

.cmn-modal-field-err { color: var(--color-danger); font-size: 11.5px; margin: 5px 0 0; }
.cmn-modal-error {
    color: var(--color-danger); font-size: 12.5px; margin: 16px 0 0;
    background: var(--color-danger-soft); border: 1px solid var(--color-danger);
    border-radius: var(--radius-sm); padding: 8px 12px;
}

.cmn-modal-footer {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 24px 20px; border-top: 1px solid var(--color-border);
}
.cmn-modal-btn {
    padding: 9px 18px; font-size: 13px; font-weight: 600; border-radius: var(--radius-sm);
    cursor: pointer; border: 1px solid transparent; font-family: var(--font-family);
    transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.cmn-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.cmn-modal-btn--secondary { background: var(--color-surface); border-color: var(--color-border-strong); color: var(--color-text-secondary); }
.cmn-modal-btn--secondary:hover:not(:disabled) { background: var(--color-surface-hover); }
.cmn-modal-btn--primary { background: var(--gradient-brand); color: var(--color-text-inverse); }
.cmn-modal-btn--primary:hover:not(:disabled) { filter: brightness(1.06); }
.cmn-modal-btn--danger { background: var(--color-danger); color: var(--color-text-inverse); }
.cmn-modal-btn--danger:hover:not(:disabled) { filter: brightness(0.95); }

/* A caller rendering a shared CommonTable inside plain-display children sits
   in .cmn-modal-body, which -- since react-bootstrap's Modal portals to
   document.body -- is never a descendant of any page-level wrapper (e.g.
   TPCRM Portal's .tpcrm-shell). Page-level tables get 2 polish fixes scoped
   to their own shell class (last column shrunk to content instead of
   holding a big empty gutter; literal corner cells rounded, since
   border-radius has no effect on a table once border-collapse is collapse);
   an ancestor-scoped selector like that can't reach a table portalled into
   a modal, so the same 2 rules are repeated here, scoped to
   .cmn-modal-body instead, to keep every CommonTable in the app looking
   consistent whether it's on a page or inside a CommonModal. */
.cmn-modal-body .cstm_table th:last-child,
.cmn-modal-body .cstm_table td:last-child {
    width: 1%;
    white-space: nowrap;
}
.cmn-modal-body .cstm_table thead tr:first-child th:first-child { border-top-left-radius: var(--radius-sm); }
.cmn-modal-body .cstm_table thead tr:first-child th:last-child { border-top-right-radius: var(--radius-sm); }
.cmn-modal-body .cstm_table tbody tr:last-child td:first-child { border-bottom-left-radius: var(--radius-sm); }
.cmn-modal-body .cstm_table tbody tr:last-child td:last-child { border-bottom-right-radius: var(--radius-sm); }

/* Fullscreen mode — let the form fill the viewport and the body take all the
   leftover height (so dropdowns/long content have plenty of room). */
.modal-fullscreen .cmn-modal-content,
[class*="modal-fullscreen"] .cmn-modal-content { height: 100%; border-radius: 0 !important; }
.modal-fullscreen .cmn-modal-form,
[class*="modal-fullscreen"] .cmn-modal-form { height: 100%; }
.modal-fullscreen .cmn-modal-body,
[class*="modal-fullscreen"] .cmn-modal-body { max-height: none; flex: 1 1 auto; }
`;

export default CommonModal;
